import type { CanvasRenderingTarget2D } from 'fancy-canvas';
import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitiveBase,
  SeriesAttachedParameter,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';
import type { Candle } from '../types';

interface BollingerFillPoint {
  time: UTCTimestamp;
  upper: number;
  lower: number;
}

type PrimitiveAttachment = SeriesAttachedParameter<Time, 'Candlestick'>;

class BollingerBandsFillPaneRenderer implements IPrimitivePaneRenderer {
  public constructor(private readonly source: BollingerBandsFillPrimitive) {}

  public draw(target: CanvasRenderingTarget2D): void {
    const attachment = this.source.attachment;
    if (!attachment || this.source.points.length < 2 || this.source.fillOpacity <= 0) {
      return;
    }

    const { chart, series } = attachment;

    target.useMediaCoordinateSpace(({ context }) => {
      context.save();
      context.fillStyle = this.source.fillColor;
      context.globalAlpha = this.source.fillOpacity;

      let upperSegment: { x: number; y: number }[] = [];
      let lowerSegment: { x: number; y: number }[] = [];

      const flushSegment = () => {
        if (upperSegment.length < 2 || lowerSegment.length < 2) {
          upperSegment = [];
          lowerSegment = [];
          return;
        }

        context.beginPath();
        context.moveTo(upperSegment[0].x, upperSegment[0].y);
        for (let index = 1; index < upperSegment.length; index += 1) {
          context.lineTo(upperSegment[index].x, upperSegment[index].y);
        }
        for (let index = lowerSegment.length - 1; index >= 0; index -= 1) {
          context.lineTo(lowerSegment[index].x, lowerSegment[index].y);
        }
        context.closePath();
        context.fill();

        upperSegment = [];
        lowerSegment = [];
      };

      for (const point of this.source.points) {
        const x = chart.timeScale().timeToCoordinate(point.time);
        const upperY = series.priceToCoordinate(point.upper);
        const lowerY = series.priceToCoordinate(point.lower);

        if (x === null || upperY === null || lowerY === null) {
          flushSegment();
          continue;
        }

        upperSegment.push({ x, y: upperY });
        lowerSegment.push({ x, y: lowerY });
      }

      flushSegment();
      context.restore();
    });
  }
}

class BollingerBandsFillPaneView implements IPrimitivePaneView {
  private readonly paneRenderer: BollingerBandsFillPaneRenderer;

  public constructor(source: BollingerBandsFillPrimitive) {
    this.paneRenderer = new BollingerBandsFillPaneRenderer(source);
  }

  public zOrder() {
    return 'bottom' as const;
  }

  public renderer(): IPrimitivePaneRenderer {
    return this.paneRenderer;
  }
}

export class BollingerBandsFillPrimitive implements ISeriesPrimitiveBase<PrimitiveAttachment> {
  public attachment: PrimitiveAttachment | null = null;
  public points: BollingerFillPoint[] = [];
  public fillColor = '#2962ff';
  public fillOpacity = 0.14;

  private readonly paneView: BollingerBandsFillPaneView;

  public constructor() {
    this.paneView = new BollingerBandsFillPaneView(this);
  }

  public attached(param: PrimitiveAttachment): void {
    this.attachment = param;
  }

  public detached(): void {
    this.attachment = null;
  }

  public paneViews() {
    return [this.paneView];
  }

  public setData(
    candles: Candle[],
    upper: (number | undefined)[],
    lower: (number | undefined)[],
    fillColor: string,
    fillOpacity: number,
  ): void {
    const points: BollingerFillPoint[] = [];

    for (let index = 0; index < candles.length; index += 1) {
      const upperValue = upper[index];
      const lowerValue = lower[index];
      if (upperValue === undefined || lowerValue === undefined) {
        continue;
      }
      if (!Number.isFinite(upperValue) || !Number.isFinite(lowerValue)) {
        continue;
      }

      points.push({
        time: Math.floor(new Date(candles[index].time).getTime() / 1000) as UTCTimestamp,
        upper: upperValue,
        lower: lowerValue,
      });
    }

    this.points = points;
    this.fillColor = fillColor;
    this.fillOpacity = fillOpacity;
    this.attachment?.requestUpdate();
  }
}
